/** Canvas Size dialog.
 *
 *  Image → Canvas Size opens this dialog. Sets new canvas dimensions
 *  without resampling pixels — every layer stays at its current
 *  resolution and gets shifted into the new canvas based on the
 *  user-picked anchor. Layers extending beyond the new bounds are
 *  visually clipped by Fabric at render / export time; they aren't
 *  destructively cropped, so undoing the resize restores everything.
 *
 *  Distinct from:
 *   - Crop (Tier 1 group A) — extracts a region into a new bg image,
 *     destructive to surrounding pixels.
 *   - Image Size (Tier 4, not yet built) — would resample pixels to
 *     the new dimensions, changing the image's intrinsic resolution.
 */

import { useEffect, useRef, useState } from 'react';
import { ImageIcon, X } from 'lucide-react';
import { MODAL_OVERLAY, MODAL_SHEET_CHROME } from '../lib/modal-classes';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Current canvas dimensions — used to seed inputs and to compute
   *  the delta shown in the readout. */
  currentWidth: number;
  currentHeight: number;
  /** Called when the user confirms. The parent applies the resize
   *  via the canvas hook and writes a history entry. `fillColor`
   *  is the hex value picked in the Extension fill section, or
   *  `null` for transparent (no canvas background fill). */
  onApply: (
    newWidth: number,
    newHeight: number,
    anchorX: AnchorFactor,
    anchorY: AnchorFactor,
    fillColor: string | null,
  ) => void;
}

/** Anchor factors are the three-position values used to shift the
 *  existing pixels relative to the new canvas bounds. 0 = pin to
 *  the left/top (expansion grows to the right/bottom), 0.5 = center
 *  (expand evenly), 1 = pin to the right/bottom (expand to the
 *  left/top). The 3×3 grid is a (factorX, factorY) pair. */
export type AnchorFactor = 0 | 0.5 | 1;

const ANCHOR_FACTORS: readonly AnchorFactor[] = [0, 0.5, 1];

/** Extension fill choices the dialog exposes. `null` is the
 *  "transparent" sentinel — the canvas backgroundColor is cleared,
 *  showing whatever's behind (pasteboard or the user's transparency
 *  checker if enabled). Hex values become the Fabric canvas
 *  background fill. Custom color picker is deferred. */
type FillChoice = { id: string; label: string; color: string | null };

const FILL_CHOICES: readonly FillChoice[] = [
  { id: 'white', label: 'White', color: '#ffffff' },
  { id: 'black', label: 'Black', color: '#000000' },
  { id: 'transparent', label: 'Transparent', color: null },
];

export function CanvasSizeDialog({ open, onClose, currentWidth, currentHeight, onApply }: Props) {
  const [widthDraft, setWidthDraft] = useState<string>(String(currentWidth));
  const [heightDraft, setHeightDraft] = useState<string>(String(currentHeight));
  // Default anchor: center. Most users expanding a canvas want the
  // existing image to stay centered.
  const [anchorX, setAnchorX] = useState<AnchorFactor>(0.5);
  const [anchorY, setAnchorY] = useState<AnchorFactor>(0.5);
  // Default fill: White. Matches Photoshop's "Background color" default
  // for the common case of expanding an opaque-source doc (JPEG / PNG
  // with bg layer). Layered/transparent users can pick Transparent.
  const [fillChoiceId, setFillChoiceId] = useState<string>('white');
  const [error, setError] = useState<string | null>(null);

  // Re-seed when the dialog re-opens (e.g. opened against a different
  // document tab, or after a prior resize landed). Same pattern as
  // DocumentSettingsDialog — gate on the open transition so a parent
  // re-render mid-edit doesn't blow away the user's drafts.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setWidthDraft(String(currentWidth));
      setHeightDraft(String(currentHeight));
      setAnchorX(0.5);
      setAnchorY(0.5);
      setFillChoiceId('white');
      setError(null);
    }
    prevOpenRef.current = open;
  }, [open, currentWidth, currentHeight]);

  // Close on Escape — matches the rest of the editor's modals.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const parsedW = Number(widthDraft);
  const parsedH = Number(heightDraft);
  const isValid =
    Number.isFinite(parsedW) &&
    Number.isFinite(parsedH) &&
    parsedW >= 1 &&
    parsedH >= 1 &&
    parsedW <= 32000 &&
    parsedH <= 32000;
  const isUnchanged = parsedW === currentWidth && parsedH === currentHeight;

  const fillChoice = FILL_CHOICES.find((c) => c.id === fillChoiceId) ?? FILL_CHOICES[0]!;

  const handleApply = () => {
    if (!isValid) {
      setError('Width and height must be whole numbers between 1 and 32000 pixels.');
      return;
    }
    if (isUnchanged) {
      // Even when dimensions don't change, the user may have picked a
      // new fill — apply it so a Cancel-style no-op doesn't silently
      // discard that pick.
      onApply(currentWidth, currentHeight, anchorX, anchorY, fillChoice.color);
      onClose();
      return;
    }
    setError(null);
    onApply(Math.round(parsedW), Math.round(parsedH), anchorX, anchorY, fillChoice.color);
    onClose();
  };

  const deltaW = parsedW - currentWidth;
  const deltaH = parsedH - currentHeight;
  const formatDelta = (n: number) => (n > 0 ? `+${n}` : String(n));

  return (
    <div className={MODAL_OVERLAY} onClick={onClose} role="presentation">
      <div
        role="dialog"
        aria-label="Canvas size"
        aria-modal="true"
        className={`bg-background shadow-xl ${MODAL_SHEET_CHROME} w-full sm:w-[92vw] max-w-md max-h-[90vh] flex flex-col overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
          <ImageIcon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-semibold flex-1">Canvas size</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-2 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4">
          <section aria-labelledby="canvas-current-label" className="space-y-1.5">
            <h3
              id="canvas-current-label"
              className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide"
            >
              Current
            </h3>
            <div className="text-sm tabular-nums">
              {currentWidth} × {currentHeight} px
            </div>
          </section>

          <section aria-labelledby="canvas-new-label" className="space-y-2">
            <h3
              id="canvas-new-label"
              className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide"
            >
              New
            </h3>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 flex-1">
                <span className="text-xs w-12 text-muted-foreground">Width</span>
                <input
                  type="number"
                  min={1}
                  max={32000}
                  step={1}
                  value={widthDraft}
                  onChange={(e) => setWidthDraft(e.target.value)}
                  className="flex-1 text-sm bg-background border border-border rounded px-2 py-1 focus:border-primary focus:outline-none tabular-nums"
                  aria-label="New canvas width in pixels"
                />
                <span className="text-xs text-muted-foreground w-6">px</span>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 flex-1">
                <span className="text-xs w-12 text-muted-foreground">Height</span>
                <input
                  type="number"
                  min={1}
                  max={32000}
                  step={1}
                  value={heightDraft}
                  onChange={(e) => setHeightDraft(e.target.value)}
                  className="flex-1 text-sm bg-background border border-border rounded px-2 py-1 focus:border-primary focus:outline-none tabular-nums"
                  aria-label="New canvas height in pixels"
                />
                <span className="text-xs text-muted-foreground w-6">px</span>
              </label>
            </div>
            {isValid && !isUnchanged && (
              <p className="text-[11px] text-muted-foreground tabular-nums">
                Δ {formatDelta(deltaW)} × {formatDelta(deltaH)} px
              </p>
            )}
          </section>

          <section aria-labelledby="canvas-anchor-label" className="space-y-2">
            <h3
              id="canvas-anchor-label"
              className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide"
            >
              Anchor
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Where the existing image stays as the canvas grows or shrinks.
            </p>
            <AnchorGrid
              anchorX={anchorX}
              anchorY={anchorY}
              onChange={(x, y) => {
                setAnchorX(x);
                setAnchorY(y);
              }}
            />
          </section>

          <section aria-labelledby="canvas-fill-label" className="space-y-2">
            <h3
              id="canvas-fill-label"
              className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide"
            >
              Extension fill
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Color shown wherever no layer covers the canvas — including the new area created by an
              expansion.
            </p>
            <div role="radiogroup" aria-labelledby="canvas-fill-label" className="flex gap-1.5">
              {FILL_CHOICES.map((choice) => {
                const isSelected = choice.id === fillChoiceId;
                return (
                  <button
                    key={choice.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setFillChoiceId(choice.id)}
                    className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-md border ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:bg-accent text-muted-foreground'
                    }`}
                  >
                    {choice.color === null ? (
                      // Checkerboard square = transparent. CSS-only,
                      // matches the transparency-checker overlay style.
                      <span
                        aria-hidden="true"
                        className="w-4 h-4 rounded border border-border"
                        style={{
                          backgroundImage:
                            'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                          backgroundSize: '8px 8px',
                          backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                        }}
                      />
                    ) : (
                      <span
                        aria-hidden="true"
                        className="w-4 h-4 rounded border border-border"
                        style={{ backgroundColor: choice.color }}
                      />
                    )}
                    {choice.label}
                  </button>
                );
              })}
            </div>
          </section>

          <p className="text-[11px] text-muted-foreground italic">
            Pixels aren&apos;t resampled. Layers extending beyond the new bounds are clipped at
            render time — undo restores them.
          </p>

          {error && (
            <div
              role="alert"
              className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded px-2 py-1.5"
            >
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md hover:bg-accent text-muted-foreground"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!isValid}
            className="text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

/** 3×3 anchor picker. Each cell represents (anchorX, anchorY) as a
 *  factor in {0, 0.5, 1}. Selected cell is filled; the others render
 *  small arrows pointing OUTWARD from the selection so the user can
 *  see "the canvas will grow this way" at a glance. */
function AnchorGrid({
  anchorX,
  anchorY,
  onChange,
}: {
  anchorX: AnchorFactor;
  anchorY: AnchorFactor;
  onChange: (x: AnchorFactor, y: AnchorFactor) => void;
}) {
  return (
    <div
      role="grid"
      aria-label="Anchor position"
      className="inline-grid grid-cols-3 gap-1 p-1 bg-muted/40 rounded-md"
    >
      {ANCHOR_FACTORS.map((y) =>
        ANCHOR_FACTORS.map((x) => {
          const isSelected = x === anchorX && y === anchorY;
          return (
            <button
              key={`${x}-${y}`}
              type="button"
              role="gridcell"
              aria-selected={isSelected}
              aria-label={labelFor(x, y)}
              onClick={() => onChange(x, y)}
              className={`w-8 h-8 rounded border transition-colors ${
                isSelected
                  ? 'bg-primary border-primary'
                  : 'bg-background border-border hover:bg-accent'
              }`}
            />
          );
        }),
      )}
    </div>
  );
}

function labelFor(x: AnchorFactor, y: AnchorFactor): string {
  const yLabel = y === 0 ? 'top' : y === 1 ? 'bottom' : 'middle';
  const xLabel = x === 0 ? 'left' : x === 1 ? 'right' : 'center';
  if (x === 0.5 && y === 0.5) return 'Center';
  return `${yLabel[0]!.toUpperCase()}${yLabel.slice(1)}-${xLabel}`;
}
